import { useEffect, useState, useCallback } from "react";
import { Loader2, LogOut, RefreshCw, Trash2, Truck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "istay_admin_token";

const inputCls =
  "w-full bg-[#0D0D0D] border border-neutral-700 px-4 py-4 text-white placeholder-neutral-600 outline-none transition-colors duration-200 focus:border-orange-500";

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof data.detail === "string" ? data.detail : "Login failed");
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-5">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md border border-neutral-800 bg-[#121212] p-10"
      >
        <img src="/logo.png" alt="I Stay Trucking" className="h-12 w-auto" />
        <h1 className="mt-8 font-display text-3xl font-bold uppercase tracking-tight text-white">
          Admin Access
        </h1>
        <p className="mt-2 text-sm text-neutral-500">Authorized personnel only.</p>

        <div className="mt-8 space-y-5">
          <div>
            <label htmlFor="admin-email" className="block mb-2 text-xs font-bold uppercase tracking-[0.15em] text-neutral-400">
              Email
            </label>
            <input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              required
              className={inputCls}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@email.com"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="block mb-2 text-xs font-bold uppercase tracking-[0.15em] text-neutral-400">
              Password
            </label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              required
              className={inputCls}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="********"
            />
          </div>
        </div>

        {error && (
          <p data-testid="admin-login-error" className="mt-6 border border-red-500/50 bg-red-500/10 px-4 py-3 text-sm text-red-400">
            {error}
          </p>
        )}

        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={loading}
          className="mt-8 flex w-full items-center justify-center gap-2 bg-orange-500 px-6 py-4 font-display text-base font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-orange-600 disabled:opacity-60"
        >
          {loading ? <Loader2 size={20} className="animate-spin" /> : "Log In"}
        </button>

        <a href="/" data-testid="admin-back-link" className="mt-6 block text-center text-xs uppercase tracking-wider text-neutral-600 hover:text-orange-500 transition-colors duration-200">
          Back to website
        </a>
      </form>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        onLogout();
        return;
      }
      if (!res.ok) throw new Error("Failed to load leads");
      setLeads(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    await fetch(`${API}/leads/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    setLeads((prev) => prev.filter((l) => l.id !== id));
  };

  const columns = ["Name", "Phone", "Email", "Location", "Profile", "Goal", "Capital", "Timeline", "Submitted", ""];

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="border-b border-neutral-800 bg-black/80">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="I Stay Trucking" className="h-10 w-auto" />
            <span className="hidden sm:inline font-display text-sm font-semibold uppercase tracking-[0.2em] text-orange-500">
              Leads Dashboard
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh-button"
              className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-xs font-bold uppercase tracking-wider text-neutral-300 transition-colors duration-200 hover:border-orange-500 hover:text-orange-500"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-xs font-bold uppercase tracking-wider text-neutral-300 transition-colors duration-200 hover:border-red-500 hover:text-red-500"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-bold uppercase tracking-tight text-white" data-testid="admin-dashboard-title">
              Submitted Leads
            </h1>
            <p className="mt-2 text-sm text-neutral-500" data-testid="admin-lead-count">
              {leads.length} lead{leads.length === 1 ? "" : "s"} total
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20" data-testid="admin-loading">
            <Loader2 size={32} className="animate-spin text-orange-500" />
          </div>
        ) : error ? (
          <p data-testid="admin-error" className="border border-red-500/50 bg-red-500/10 px-4 py-3 text-sm text-red-400">{error}</p>
        ) : leads.length === 0 ? (
          <div className="border border-neutral-800 bg-[#121212] p-16 text-center" data-testid="admin-empty-state">
            <Truck size={40} className="mx-auto text-neutral-700" />
            <p className="mt-6 font-display text-xl font-semibold uppercase tracking-wide text-neutral-400">
              No leads yet
            </p>
            <p className="mt-2 text-sm text-neutral-600">
              New applications from the website will appear here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-neutral-800" data-testid="admin-leads-table-wrapper">
            <table className="w-full min-w-[1100px] text-left text-sm" data-testid="admin-leads-table">
              <thead>
                <tr className="border-b border-neutral-800 bg-[#121212]">
                  {columns.map((h) => (
                    <th key={h} className="px-4 py-4 font-display text-xs font-bold uppercase tracking-[0.15em] text-neutral-400">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.id} data-testid={`lead-row-${lead.id}`} className="border-b border-neutral-800/60 bg-[#0A0A0A] transition-colors duration-150 hover:bg-[#121212]">
                    <td className="px-4 py-4 font-semibold text-white whitespace-nowrap">
                      {lead.first_name} {lead.last_name}
                    </td>
                    <td className="px-4 py-4 text-neutral-300 whitespace-nowrap">{lead.phone}</td>
                    <td className="px-4 py-4 text-neutral-300">{lead.email}</td>
                    <td className="px-4 py-4 text-neutral-300 whitespace-nowrap">
                      {lead.city}, {lead.state}
                    </td>
                    <td className="px-4 py-4 text-neutral-300">{lead.describe_you || "—"}</td>
                    <td className="px-4 py-4 text-neutral-300">{lead.goal || "—"}</td>
                    <td className="px-4 py-4 text-neutral-300">{lead.capital || "—"}</td>
                    <td className="px-4 py-4 text-neutral-300 whitespace-nowrap">{lead.begin_timeline || "—"}</td>
                    <td className="px-4 py-4 text-neutral-500 whitespace-nowrap">
                      {new Date(lead.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => remove(lead.id)}
                        data-testid={`lead-delete-${lead.id}`}
                        aria-label="Delete lead"
                        className="text-neutral-600 transition-colors duration-150 hover:text-red-500"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  return token ? (
    <Dashboard token={token} onLogout={logout} />
  ) : (
    <LoginForm onLogin={setToken} />
  );
}
